import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from './LanguageContext';
import { t } from '../utils/translations';
import { Check } from 'lucide-react';
import { QuoteModal } from './QuoteModal';
export function PricingTable() {
  const {
    lang
  } = useLanguage();
  const text = t[lang].pricing;
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('');
  const openQuote = (title: string) => {
    setSelectedPackage(title);
    setModalOpen(true);
  };
  return <section id="pricing" className="py-24 bg-mf-gray relative overflow-hidden" data-id="element-402">
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-gray-100 to-transparent opacity-50 pointer-events-none" data-id="element-403"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-404">
        <div className="text-center mb-20" data-id="element-405">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          stiffness: 100,
          damping: 15
        }} className="text-4xl md:text-6xl font-condensed font-black text-mf-black uppercase tracking-tight mb-6" data-id="element-406">
            {text.title}
          </motion.h2>
          <motion.div initial={{
          width: 0
        }} whileInView={{
          width: 96
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2,
          duration: 0.8
        }} className="h-1.5 bg-mf-red mx-auto rounded-full mb-6" data-id="element-407"></motion.div>
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-408">
            {text.subtitle}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8" data-id="element-409">
          {text.cards.map((card, index) => <motion.div key={index} initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} whileInView={{
          opacity: 1,
          scale: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1,
          type: 'spring',
          stiffness: 100,
          damping: 15
        }} whileHover={{
          y: -6
        }} className="relative group flex" data-id="element-410">
              {/* Gradient border wrapper */}
              <div className="relative w-full rounded-3xl shadow-xl group-hover:shadow-2xl transition-shadow duration-500 bg-gradient-to-br from-mf-red via-mf-yellow to-orange-500 flex" style={{
            padding: '3px'
          }} data-id="element-411">
                {/* Animated glow on hover */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-mf-red via-mf-yellow to-orange-500 opacity-0 group-hover:opacity-60 transition-opacity duration-500 blur-xl -z-10" aria-hidden="true" data-id="element-412"></div>

                {/* Inner card */}
                <div className="w-full bg-white rounded-[22px] relative flex flex-col overflow-hidden" data-id="element-413">
                  {/* Top accent bar */}
                  <div className="h-2 w-full bg-gradient-to-r from-mf-red via-mf-yellow to-orange-500" data-id="element-414"></div>
                  <CardContent card={card} ctaText={text.cta} onCta={() => openQuote(card.title)} data-id="element-415" />
                </div>
              </div>
            </motion.div>)}
        </div>

        <p className="text-center text-sm text-gray-500 mt-10 italic max-w-2xl mx-auto" data-id="element-416">
          {text.note}
        </p>
      </div>

      <QuoteModal open={modalOpen} onClose={() => setModalOpen(false)} packageTitle={selectedPackage} data-id="element-417" />
    </section>;
}
function CardContent({
  card,
  ctaText,
  onCta
}: {
  card: any;
  ctaText: string;
  onCta: () => void;
}) {
  return <>
      <div className="p-8 flex-grow flex flex-col" data-id="element-418">
        <h3 className="text-2xl font-condensed font-black uppercase text-mf-black mb-6 border-b-2 border-gray-100 pb-4 min-h-[3.5rem]" data-id="element-419">
          {card.title}
        </h3>

        <ul className="space-y-4 mb-8 flex-grow" data-id="element-420">
          {card.features.map((feature: string, fIndex: number) => <li key={fIndex} className="flex items-start group/item" data-id="element-421">
              <div className="mt-0.5 mr-3 flex-shrink-0 w-6 h-6 rounded-full bg-mf-red/10 text-mf-red group-hover/item:bg-mf-red group-hover/item:text-white flex items-center justify-center transition-colors duration-300" data-id="element-422">
                <Check size={14} strokeWidth={3} data-id="element-423" />
              </div>
              <span className="text-gray-700 font-medium leading-tight pt-0.5 text-sm" data-id="element-424">
                {feature}
              </span>
            </li>)}
        </ul>
      </div>

      <div className="p-8 pt-0 mt-auto" data-id="element-425">
        <button type="button" onClick={onCta} className="block w-full text-center py-4 rounded-xl font-condensed font-bold text-lg uppercase tracking-wider transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] bg-mf-red text-white shadow-[0_4px_14px_0_rgba(211,47,47,0.39)] hover:bg-red-700 hover:shadow-[0_6px_20px_rgba(211,47,47,0.45)]" data-id="element-426">
          {ctaText}
        </button>
      </div>
    </>;
}